import { RegisterOptions } from 'react-hook-form';
import { z } from 'zod';

/**
 * Convert React Hook Form RegisterOptions to Zod schema
 * This is a helper for migrating existing validation rules
 */
export function convertRulesToZod(
  rules?: RegisterOptions,
  fieldType: 'string' | 'number' | 'email' = 'string'
): z.ZodTypeAny {
  let schema: z.ZodTypeAny;

  // Start with base schema based on field type
  switch (fieldType) {
    case 'number':
      schema = z.number();
      break;
    case 'email':
      schema = z.string().email();
      break;
    default:
      schema = z.string();
  }

  if (!rules) {
    return schema;
  }

  // Apply required validation
  if (rules.required) {
    if (fieldType === 'number') {
      schema = (schema as z.ZodNumber).refine(
        val => val !== undefined && val !== null,
        {
          message:
            typeof rules.required === 'string'
              ? rules.required
              : 'This field is required',
        }
      );
    } else {
      schema = (schema as z.ZodString).min(1, {
        message:
          typeof rules.required === 'string'
            ? rules.required
            : 'This field is required',
      });
    }
  }

  // Apply min length
  if (rules.minLength && fieldType === 'string') {
    const minLengthValue =
      typeof rules.minLength === 'number'
        ? rules.minLength
        : typeof rules.minLength === 'object' && 'value' in rules.minLength
          ? rules.minLength.value
          : rules.minLength;
    const minLengthMessage =
      typeof rules.minLength === 'number'
        ? undefined
        : typeof rules.minLength === 'object' && 'message' in rules.minLength
          ? (rules.minLength.message as string | undefined)
          : undefined;

    schema = (schema as z.ZodString).min(minLengthValue, {
      message: minLengthMessage ?? `Minimum length is ${minLengthValue}`,
    });
  }

  // Apply max length
  if (rules.maxLength && fieldType === 'string') {
    const maxLengthValue =
      typeof rules.maxLength === 'number'
        ? rules.maxLength
        : typeof rules.maxLength === 'object' && 'value' in rules.maxLength
          ? rules.maxLength.value
          : rules.maxLength;
    const maxLengthMessage =
      typeof rules.maxLength === 'number'
        ? undefined
        : typeof rules.maxLength === 'object' && 'message' in rules.maxLength
          ? (rules.maxLength.message as string | undefined)
          : undefined;

    schema = (schema as z.ZodString).max(maxLengthValue, {
      message: maxLengthMessage ?? `Maximum length is ${maxLengthValue}`,
    });
  }

  // Apply min value
  if (rules.min && fieldType === 'number') {
    const minValueRaw =
      typeof rules.min === 'number'
        ? rules.min
        : typeof rules.min === 'object' && 'value' in rules.min
          ? rules.min.value
          : rules.min;
    const minValue =
      typeof minValueRaw === 'number' ? minValueRaw : Number(minValueRaw) || 0;
    const minMessage =
      typeof rules.min === 'number'
        ? undefined
        : typeof rules.min === 'object' && 'message' in rules.min
          ? (rules.min.message as string | undefined)
          : undefined;

    schema = (schema as z.ZodNumber).min(minValue, {
      message: minMessage ?? `Minimum value is ${minValue}`,
    });
  }

  // Apply max value
  if (rules.max && fieldType === 'number') {
    const maxValueRaw =
      typeof rules.max === 'number'
        ? rules.max
        : typeof rules.max === 'object' && 'value' in rules.max
          ? rules.max.value
          : rules.max;
    const maxValue =
      typeof maxValueRaw === 'number' ? maxValueRaw : Number(maxValueRaw) || 0;
    const maxMessage =
      typeof rules.max === 'number'
        ? undefined
        : typeof rules.max === 'object' && 'message' in rules.max
          ? (rules.max.message as string | undefined)
          : undefined;

    schema = (schema as z.ZodNumber).max(maxValue, {
      message: maxMessage ?? `Maximum value is ${maxValue}`,
    });
  }

  // Apply pattern
  if (rules.pattern && fieldType === 'string') {
    const patternValue =
      rules.pattern instanceof RegExp
        ? rules.pattern
        : typeof rules.pattern === 'object' && 'value' in rules.pattern
          ? rules.pattern.value
          : rules.pattern;
    const patternMessage =
      rules.pattern instanceof RegExp
        ? undefined
        : typeof rules.pattern === 'object' && 'message' in rules.pattern
          ? (rules.pattern.message as string | undefined)
          : undefined;

    schema = (schema as z.ZodString).regex(patternValue, {
      message: patternMessage ?? 'Invalid format',
    });
  }

  return schema;
}

/**
 * Common Zod schemas for reuse
 */
export const commonSchemas = {
  email: z.string().email('Must be a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Must be a valid phone number'),
  requiredString: z.string().min(1, 'This field is required'),
  url: z.string().url('Must be a valid URL'),
};
